import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { Tag } from '../components/Tag';
import { ErroBox, PageHeader, Vazio } from '../components/ui';

interface Problema {
  linha: number;
  nome: string | null;
  telefone: string | null;
  motivo: string;
  mensagem: string;
}

interface Resultado {
  importId: string;
  dryRun: boolean;
  status: string;
  mapeamentoUsado: Record<string, string>;
  mapeamentoAutomatico: boolean;
  cabecalhoEncontrado: string[];
  resumo: {
    totalRows: number;
    imported: number;
    updated: number;
    duplicated: number;
    skipped: number;
    failed: number;
    /** Subconjunto das que entram: pessoas já conhecidas de outros eventos (A7). */
    alreadyKnown: number;
  };
  problemas: Problema[];
  amostra: {
    linha: number;
    fullName?: string;
    phoneE164?: string;
    company: string | null;
    jobTitle: string | null;
  }[];
}

const ROTULOS_CAMPO: Record<string, string> = {
  fullName: 'Nome',
  phone: 'Telefone',
  email: 'E-mail',
  jobTitle: 'Cargo',
  company: 'Empresa',
  city: 'Cidade',
  country: 'País',
  linkedin: 'LinkedIn',
  sector: 'Setor',
  segment: 'Segmento',
  participantType: 'Tipo',
  groupName: 'Grupo',
  category: 'Categoria',
  credential: 'Credencial',
  externalId: 'ID externo',
  notes: 'Observações',
};

export function ImportarParticipantes() {
  const { id: eventId = '' } = useParams();
  const navegar = useNavigate();
  const cliente = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);

  const [arquivo, setArquivo] = useState<File | null>(null);
  const [arrastando, setArrastando] = useState(false);
  const [previa, setPrevia] = useState<Resultado | null>(null);

  const enviar = useMutation({
    mutationFn: async ({ file, dryRun }: { file: File; dryRun: boolean }) => {
      const form = new FormData();
      form.append('file', file);
      form.append('dryRun', String(dryRun));
      // Reenvia o mapeamento confirmado para a gravação usar exatamente o que
      // foi conferido na prévia — a detecção automática poderia divergir se o
      // arquivo fosse trocado entre as duas etapas.
      if (!dryRun && previa) form.append('mapping', JSON.stringify(previa.mapeamentoUsado));

      return api<Resultado>(`/events/${eventId}/participants/import`, {
        method: 'POST',
        formData: form,
      });
    },
    onSuccess: (resultado) => {
      if (resultado.dryRun) {
        setPrevia(resultado);
        return;
      }

      cliente.invalidateQueries({ queryKey: ['participantes', eventId] });
      cliente.invalidateQueries({ queryKey: ['evento', eventId] });
      navegar(`/eventos/${eventId}/participantes`);
    },
  });

  function escolher(file: File | null | undefined) {
    if (!file) return;
    setArquivo(file);
    setPrevia(null);
    enviar.mutate({ file, dryRun: true });
  }

  return (
    <>
      <PageHeader
        titulo="Importar participantes"
        voltar={{
          para: `/eventos/${eventId}?aba=participantes`,
          texto: 'Voltar para participantes',
        }}
      />

      {!previa && (
        <div className="card">
          <div
            className={`zona-arquivo${arrastando ? ' ativa' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setArrastando(true);
            }}
            onDragLeave={() => setArrastando(false)}
            onDrop={(e) => {
              e.preventDefault();
              setArrastando(false);
              escolher(e.dataTransfer.files?.[0]);
            }}
          >
            <i
              className="ti ti-file-spreadsheet"
              style={{ fontSize: 30, color: 'var(--text-dim)' }}
            />
            <div style={{ fontWeight: 600, marginTop: 10 }}>
              {enviar.isPending ? 'Analisando planilha…' : 'Arraste a planilha ou clique para escolher'}
            </div>
            <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>
              CSV ou XLSX — até 10MB e 50 mil linhas. Arquivo .xls antigo: salve como .xlsx
              antes.
            </div>

            <input
              ref={inputRef}
              type="file"
              accept=".csv,.xlsx"
              className="sr-only"
              onChange={(e) => escolher(e.target.files?.[0])}
            />
          </div>

          <div className="aviso" style={{ marginTop: 16 }}>
            <i className="ti ti-shield-check" />
            <span>
              Nada é gravado neste passo. A planilha é analisada e você confere o resultado
              antes de confirmar.
            </span>
          </div>
        </div>
      )}

      {enviar.isError && (
        <div style={{ marginTop: previa ? 16 : 0 }}>
          <ErroBox erro={enviar.error} />
        </div>
      )}

      {previa && <Previa previa={previa} arquivo={arquivo} enviar={enviar} aoTrocar={() => setPrevia(null)} />}
    </>
  );
}

function Previa({
  previa,
  arquivo,
  enviar,
  aoTrocar,
}: {
  previa: Resultado;
  arquivo: File | null;
  enviar: ReturnType<typeof useMutation<Resultado, Error, { file: File; dryRun: boolean }>>;
  aoTrocar: () => void;
}) {
  const { resumo } = previa;
  const importaveis = resumo.skipped; // no dry-run, os válidos ficam em `skipped`
  const naoMapeados = previa.cabecalhoEncontrado.filter(
    (c) => !Object.values(previa.mapeamentoUsado).includes(c),
  );

  return (
    <>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="secao-titulo">
          <i className="ti ti-list-check" /> Conferência — {arquivo?.name}
        </div>

        <div className="resumo-import">
          <ItemResumo numero={resumo.totalRows} rotulo="Linhas" />
          <ItemResumo numero={importaveis} rotulo="Serão criados" destaque="success" />
          <ItemResumo numero={resumo.duplicated} rotulo="Duplicados" destaque="warning" />
          <ItemResumo numero={resumo.failed} rotulo="Com erro" destaque="danger" />
        </div>

        {importaveis === 0 ? (
          <div className="aviso">
            <i className="ti ti-alert-triangle" />
            <span>
              Nenhuma linha pode ser importada. Corrija os problemas abaixo e envie de novo.
            </span>
          </div>
        ) : (
          <div className="aviso">
            <i className="ti ti-info-circle" />
            <span>
              Duplicados e linhas com erro são <strong>ignorados</strong>, não bloqueiam a
              importação. As {importaveis} linhas válidas serão criadas.
            </span>
          </div>
        )}

        {/*
          O ganho da decisão A7 aparece aqui, e em nenhum outro lugar da tela:
          quem roda a segunda edição de um evento precisa saber que boa parte da
          planilha não vai recomeçar do zero.
        */}
        {resumo.alreadyKnown > 0 && (
          <div className="aviso">
            <i className="ti ti-user-check" />
            <span>
              <strong>
                {resumo.alreadyKnown} {resumo.alreadyKnown === 1 ? 'pessoa já é' : 'pessoas já são'}{' '}
                conhecida{resumo.alreadyKnown === 1 ? '' : 's'}
              </strong>{' '}
              de outros eventos desta organização. {resumo.alreadyKnown === 1 ? 'Ela entra' : 'Elas entram'}{' '}
              com o perfil e o consentimento que já {resumo.alreadyKnown === 1 ? 'tem' : 'têm'} — a
              conversa continua de onde parou, em vez de começar de novo.
            </span>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
          <button className="btn btn-secondary" onClick={aoTrocar}>
            Escolher outra planilha
          </button>
          <button
            className="btn btn-primary"
            disabled={importaveis === 0 || enviar.isPending}
            onClick={() => arquivo && enviar.mutate({ file: arquivo, dryRun: false })}
          >
            {enviar.isPending ? 'Importando…' : `Confirmar e importar ${importaveis}`}
          </button>
        </div>
      </div>

      <div className="grade-detalhe">
        <div className="card">
          <div className="secao-titulo">
            <i className="ti ti-eye" /> Amostra do que será criado
          </div>

          {previa.amostra.length === 0 ? (
            <Vazio icone="ti-eye-off" titulo="Nenhuma linha válida para mostrar" />
          ) : (
            <div className="tabela-envolvente">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Linha</th>
                    <th>Nome</th>
                    <th>WhatsApp (normalizado)</th>
                    <th>Empresa</th>
                  </tr>
                </thead>
                <tbody>
                  {previa.amostra.map((linha) => (
                    <tr key={linha.linha}>
                      <td style={{ color: 'var(--text-dim)' }}>{linha.linha}</td>
                      <td className="celula-principal">{linha.fullName}</td>
                      <td className="mono">{linha.phoneE164}</td>
                      <td>
                        {linha.company || <span style={{ color: 'var(--text-dim)' }}>—</span>}
                        {linha.jobTitle && <div className="celula-secundaria">{linha.jobTitle}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card">
          <div className="secao-titulo">
            <i className="ti ti-arrows-exchange" /> Colunas reconhecidas
          </div>

          {previa.mapeamentoAutomatico && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
              Detectadas automaticamente pelos cabeçalhos. Confira antes de confirmar.
            </div>
          )}

          <dl className="lista-dados">
            {Object.entries(previa.mapeamentoUsado).map(([campo, coluna]) => (
              <div key={campo} style={{ display: 'contents' }}>
                <dt>{ROTULOS_CAMPO[campo] ?? campo}</dt>
                <dd className="mono">{coluna}</dd>
              </div>
            ))}
          </dl>

          {naoMapeados.length > 0 && (
            <>
              <div className="divider" style={{ margin: '16px 0' }} />
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                Colunas ignoradas: {naoMapeados.join(', ')}
              </div>
            </>
          )}
        </div>
      </div>

      {previa.problemas.length > 0 && (
        <div className="card" style={{ marginTop: 16, padding: 0 }}>
          <div className="secao-titulo" style={{ padding: '16px 16px 0' }}>
            <i className="ti ti-alert-circle" /> Linhas não importadas ({previa.problemas.length})
          </div>

          <div className="tabela-envolvente" style={{ maxHeight: 420, overflowY: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Linha</th>
                  <th>Nome</th>
                  <th>Telefone</th>
                  <th>Motivo</th>
                </tr>
              </thead>
              <tbody>
                {previa.problemas.map((p) => (
                  <tr key={`${p.linha}-${p.motivo}`}>
                    <td style={{ color: 'var(--text-dim)' }}>{p.linha}</td>
                    <td>{p.nome || <span style={{ color: 'var(--text-dim)' }}>—</span>}</td>
                    <td className="mono">
                      {p.telefone || <span style={{ color: 'var(--text-dim)' }}>—</span>}
                    </td>
                    <td>
                      <Tag type={p.motivo.startsWith('duplicad') || p.motivo === 'ja_existe_no_evento' ? 'warning' : 'danger'}>
                        {p.mensagem}
                      </Tag>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

function ItemResumo({
  numero,
  rotulo,
  destaque,
}: {
  numero: number;
  rotulo: string;
  destaque?: 'success' | 'warning' | 'danger';
}) {
  const cor = numero > 0 && destaque ? `var(--${destaque})` : 'var(--text)';

  return (
    <div className="resumo-item">
      <div className="resumo-numero" style={{ color: cor }}>
        {numero}
      </div>
      <div className="resumo-rotulo">{rotulo}</div>
    </div>
  );
}
